<template>
  <AuthTemplate v-if="!flashcards">
    <div slot="body">
      <h1 class="h4">
        Forgotten Password
      </h1>
      <p class="text">
        Please provide your email and we’ll send you a link to set your new password.
      </p>
      <div class="input-group">
        <input
          id="email"
          v-model="email"
          type="email"
          placeholder="Email"
          name="email"
        >
      </div>
      <VueRecaptcha
        ref="recaptcha"
        class="recaptcha"
        size="invisible"
        :sitekey="$config.reCaptchaSiteKey"
        :load-recaptcha-script="true"
        @verify="requestNewPassword"
        @expired="onRecaptchaExpired"
      >
        <button class="btn-primary btn-lg" :disabled="disabled">
          Request new password
        </button>
      </VueRecaptcha>
    </div>
  </AuthTemplate>

  <div
    v-else slot="body" class="flashcards"
    :class="{ 'flashcards-study': flashcardsStudy, 'col-4': !flashcardsStudy, 'col-7': flashcardsStudy }"
  >
    <h1 v-if="!flashcardsStudy" class="h5">
      Forgotten Password
    </h1>
    <div v-else class="arrow-heading">
      <ArrowLeft @click="$emit('email-choice')" />
      <h1 class="h4">
        Forgotten Password
      </h1>
    </div>
    <p class="text">
      Please provide your email and we’ll send you a link to set your new password.
    </p>
    <div class="input-group">
      <input
        id="email"
        v-model="email"
        type="email"
        placeholder="Email"
        name="email"
      >
    </div>
    <VueRecaptcha
      ref="recaptcha"
      class="recaptcha"
      size="invisible"
      :sitekey="$config.reCaptchaSiteKey"
      :load-recaptcha-script="true"
      @verify="requestNewPassword"
      @expired="onRecaptchaExpired"
    >
      <button class="btn-primary btn-lg" :disabled="disabled">
        Request new password
      </button>
    </VueRecaptcha>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import VueRecaptcha from 'vue-recaptcha';
import AuthTemplate from '../AuthTemplate';
import IconMail from '../../assets/svg/mail.svg?vue-component';
import ArrowLeft from '../../assets/svg/flashcards-left-arrow.svg?vue-component';

export default {
  components: {
    AuthTemplate, VueRecaptcha, ArrowLeft,
  },
  mixins: [global],
  middleware({ store, redirect }) {
    if (store.state.auth.user) {
      redirect('/');
    }
  },
  props: {
    flashcards: Boolean,
    flashcardsStudy: Boolean,
  },
  data() {
    return {
      email: '',
      checkMail: false,
      checkMailText:
        "<span class='check-mail-text'>We've sent you an email with instructions to reset your password. Please make sure it didn't wind up in your Junk mail. If you aren't receiving our password reset emails, please</span>",
      disabled: true,
    };
  },
  head() {
    const meta = {
      fullPath: `${this.$config.siteUrl}${this.$route.fullPath}`,
      metaTitle: 'Forgotten password',
      metaDescription: 'Having trouble logging in? Reset your 365 Data Science password to keep learning.',
      robots: 'noindex, nofollow',
    };

    return {
      titleTemplate: `${meta.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(meta),
    };
  },
  mounted() {
    this.disabled = false;
    this.checkMail = false;
  },
  methods: {
    async requestNewPassword() {
      this.disabled = true;
      try {
        const res = await this.$axios.$post('/auth/forget-password', {
          email: this.email,
        });
        if (res !== undefined) {
          this.checkMail = true;
          await this.$store.dispatch('modals/open', {
            name: 'confirm',
            imgSrc: IconMail,
            title: 'Check Your Mail',
            message: `${this.checkMailText} <a href="mailto:appsupport@365datascience.com">contact customer support</a>`,
            cancel: '',
            confirm: 'Done',
          });
        }
      } catch (error) {
        await this.$store.dispatch('modals/open', {
          name: 'default',
          title: error.response.data.message,
        });
      }
    },
    onRecaptchaExpired() {
      this.disabled = false;
      this.$refs.recaptcha.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
h1.h4 {
  margin-top: 0;
  text-align: center;
}

p.text {
  font-size: 14px;
  color: var(--main-dark-light-4);
  margin: var(--spacing-22) 0;
  text-align: center;
}

.input-group:not(:last-of-type) {
  margin-bottom: var(--spacing-sm);
}

input {
  width: 100%;
  border-radius: 5px;
}

button {
  width: 100%;
  margin-top: var(--spacing-16);
  margin-bottom: var(--spacing-20);
}

.flashcards {
  margin: var(--spacing-50) auto var(--spacing-30) auto;
  padding: 0 42.9px;

  h1 {
    text-align: center;
  }

  button {
    margin-bottom: 0;
  }

  &.flashcards-study {
    margin: 0 auto;
    padding: 0;

    .arrow-heading {
      display: flex;
      align-items: center;

      h1 {
        margin: 0 47px 0 65px;
      }

      svg {
        cursor: pointer;
      }
    }

    .text {
      font-size: 18px;
      font-weight: 500;
      margin: var(--spacing-20) 0;
    }

    .input-group {
      margin: 0;
    }

    .btn-primary {
      height: 56px;
      margin: var(--spacing-16) 0 0;
    }
  }
}

@include bp($bp-laptop) {
  .flashcards {
    padding: 0 12.9px;

    &.flashcards-study {
      .text {
        font-size: 16px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .flashcards {
    margin-top: var(--spacing-32);
    margin-bottom: 0;
    padding: 0 241px;

    &.flashcards-study {
      .arrow-heading {
        h1 {
          margin: 0 var(--spacing-40) 0 65px;
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .flashcards {
    padding: 0 var(--spacing-32);

    &.flashcards-study {
      .arrow-heading {
        h1 {
          font-size: 21px;
          margin: 0 42px 0 var(--spacing-36);
        }
      }

      .text {
        font-size: 16px;
        margin: var(--spacing-24) 0;
      }

      button {
        margin-top: var(--spacing-20);
      }
    }
  }
}
</style>
